// Alarm inbox and alarm detail (static demo pages/Alarms.jsx, screens 13-14)
// on real data: platform alarms (open / acknowledged), closed = a recorded
// decision (src/data/closes.ts). Analysis and timeline are written from the
// data until the AI layer and escalation chains are connected.
import { useState } from "preact/hooks";
import { Link } from "wouter-preact";
import { opsApi, useSession, type AlarmRow } from "@gimasi/giothub-shell";
import Icon from "../components/Icon.jsx";
import TimeChart from "../components/TimeChart.jsx";
import { useT, type T } from "../i18n";
import { useLoad } from "../lib/load";
import { ALARM_ST, useFleet, type Fleet } from "../data/fleet";
import { REGIMES } from "../data/classes";
import { sites, type LocNode } from "../data/locations";
import { loadCloses, saveClose, type CloseRecord } from "../data/closes";
import { State } from "./common";
import { series as readSeries } from "../lib/series";

const CAUSES = ["lid", "door", "defrost", "delivery", "refrigeration", "power", "sensor"];
const ACTIONS = ["closed", "moved", "contractor", "defrost", "none"];
type Status = "open" | "ack" | "closed";
interface Row { a: AlarmRow; status: Status; close?: CloseRecord }
const sev = (type: string) => ALARM_ST[type] || "warn";
const label = (t: T, type: string) => (t("x.at." + type) === "x.at." + type ? type : t("x.at." + type));

async function inbox() {
  const from = new Date(Date.now() - 30 * 864e5).toISOString();
  const [al, closes] = await Promise.all([opsApi.alarms({ from, limit: 500 }), loadCloses(from)]);
  const rows: Row[] = (al.items as AlarmRow[]).filter((a) => a.raised !== false).map((a) => {
    const close = closes.get(a.id);
    return { a, close, status: close ? "closed" : a.state === "open" ? "open" : "ack" };
  });
  return rows;
}

export default function Alarms({ params }: { params: Record<string, string | undefined> }) {
  const t = useT();
  const f = useFleet(t.lang);
  const { data, loading, error, reload } = useLoad(inbox);
  const [tab, setTab] = useState<Status>("open");
  const all = data || [];
  const list = all.filter((r) => r.status === tab).sort((x, y) => (tab === "closed" ? Date.parse(y.close!.occurred_at) - Date.parse(x.close!.occurred_at) : (sev(x.a.type) === "crit" ? -1 : 0) - (sev(y.a.type) === "crit" ? -1 : 0) || Date.parse(x.a.utc) - Date.parse(y.a.utc)));
  const sel = all.find((r) => r.a.id === params.id) || list[0] || all.find((r) => r.status !== "closed");
  const counts = { open: all.filter((r) => r.status === "open").length, ack: all.filter((r) => r.status === "ack").length, closed: all.filter((r) => r.status === "closed").length };
  const siteOf = new Map<string, LocNode>();
  if (f) sites(f.root).forEach((s) => s.points.forEach((p) => siteOf.set(p.id, s)));
  const name = (r: Row) => f?.byId.get(r.a.device_id)?.name || r.a.device_serial;
  return (
    <div class="page" style="gap:16px">
      <div class="page-h"><div><h1>{t("nav.alarms")}</h1><div class="sub">{t("alm.summary", { o: counts.open, a: counts.ack, c: counts.closed })}</div></div></div>
      <State loading={loading && !data} error={error} />
      <div class="alarms">
        <div class="alarm-list">
          <div class="seg" style="width:100%">{(["open", "ack", "closed"] as Status[]).map((k) => <button key={k} type="button" style="flex:1" class={tab === k ? "on" : ""} onClick={() => setTab(k)}>{t("alm.tab." + k)} {counts[k]}</button>)}</div>
          {list.length === 0 && <p style="color:var(--muted)">{t("alm.none")}</p>}
          {list.map((r) => { const p = f?.byId.get(r.a.device_id); return (
            <Link key={r.a.id} href={"/alarms/" + r.a.id} class={"acard" + (sel?.a.id === r.a.id ? " on" : "") + (sev(r.a.type) === "crit" && r.status === "open" ? " hot" : "")}>
              <div class="row" style="justify-content:space-between"><span class={"st st-" + sev(r.a.type)}>{t("st." + sev(r.a.type))}</span><span class="mono" style="color:var(--muted);font-size:13px">{r.status === "closed" ? new Date(r.close!.occurred_at).toLocaleDateString(t.locale, { day: "numeric", month: "short" }) : t.dur(Math.max(0, Math.round((Date.now() - Date.parse(r.a.utc)) / 60e3)))}</span></div>
              <b>{name(r)}{sev(r.a.type) !== "delayed" ? ` · ${t.temp(p?.temp ?? null)}` : ""}</b>
              <small>{siteOf.get(r.a.device_id)?.name || ""}{" · "}{r.status === "closed" ? t("alm.cause." + r.close!.body.cause) : r.status === "ack" ? t("x.alarm." + (r.a.state || "cleared")) : label(t, r.a.type)}</small>
            </Link>); })}
        </div>
        {sel && f ? <Detail key={sel.a.id + sel.status} r={sel} f={f} site={siteOf.get(sel.a.device_id)} onChange={reload} /> : !loading && <div class="card">{t("alm.none")}</div>}
      </div>
    </div>
  );
}

function Detail({ r, f, site, onChange }: { r: Row; f: Fleet; site?: LocNode; onChange: () => void }) {
  const t = useT();
  const { user } = useSession();
  const a = r.a;
  const p = f.byId.get(a.device_id);
  const cls = p ? REGIMES[p.regime] || null : null;
  const since = Date.parse(a.utc);
  const end = r.close ? Date.parse(r.close.occurred_at) : a.cleared_utc ? Date.parse(a.cleared_utc) : Date.now();
  const from = since - 130 * 60e3, to = Math.max(end, since + 30 * 60e3);
  const ser = useLoad(async () => {
    for (const key of ["ext_temperature", "temperature_1"]) {
      try { const s = await readSeries(a.device_id, { key, from: new Date(from).toISOString(), to: new Date(Math.min(to, Date.now())).toISOString(), resolution: "raw" }); if (s.items.length) return s.items; } catch { /* next key */ }
    }
    return [];
  }, [a.id]);
  const pts = (ser.data || []).map((x) => ({ t: Date.parse(x.utc), v: x.value }));
  const during = pts.filter((x) => x.t >= since && x.t <= end);
  const peak = during.length ? Math.max(...during.map((x) => x.v)) : p?.temp ?? null;
  const mins = Math.max(0, Math.round((end - since) / 60e3));
  const [cause, setCause] = useState(a.type === "NO_DATA" ? "sensor" : "refrigeration");
  const [action, setAction] = useState(a.type === "NO_DATA" ? "none" : "contractor");
  const [probe, setProbe] = useState("");
  const [product, setProduct] = useState("none");
  const [record, setRecord] = useState(t("x.alm.draft", { type: label(t, a.type).toLowerCase(), cab: p?.name || a.device_serial, site: site?.name || "", start: t.time(new Date(since)), peak: t.temp(peak), m: mins }));
  const [sign, setSign] = useState(false);
  const [meaning, setMeaning] = useState("alm.meaning1");
  const [toast, setToast] = useState("");
  const [busy, setBusy] = useState(false);
  const say = (m: string) => { setToast(m); setTimeout(() => setToast(""), 2600); };
  const ack = async () => { const note = prompt(t("alm.ack")); if (!note) return; await opsApi.ackAlarm(a.id, note); say(t("alm.acked")); onChange(); };
  const close = async (e: Event) => {
    e.preventDefault(); setBusy(true);
    try {
      if (a.state === "open") await opsApi.ackAlarm(a.id, record.slice(0, 500));
      await saveClose(a.device_id, `${label(t, a.type)} · ${p?.name || a.device_serial}`, { alarm_id: a.id, alarm_type: a.type, cause, action, probe, product, record, meaning: t(meaning), signer: user.name || user.email });
      setSign(false); say(t("alm.closedToast")); onChange();
    } catch (err) { say(t("x.error", { m: (err as Error).message })); }
    setBusy(false);
  };
  const s = sev(a.type);
  const analysis = [
    s === "delayed" ? t("x.alm.an.nodata", { cab: p?.name || "", since: t.ago(p?.lastContact) }) : t("x.alm.an.limit", { cab: p?.name || "", peak: t.temp(peak), lim: cls ? t.temp(s === "crit" ? cls.crit : s === "alarm" ? cls.alarm : cls.warn, 1) : "—", m: mins }),
    t("x.alm.an.now", { v: t.temp(p?.temp ?? null), when: t.ago(p?.lastContact) }),
  ];
  return (
    <div class="grid" style="align-content:start;gap:16px">
      <section class="card">
        <div class="row" style="justify-content:space-between;align-items:flex-start">
          <div>
            <div class="row" style="gap:8px"><span class={"st st-" + s}>{t("st." + s)}</span><span class="mono" style="color:var(--muted)">{a.id.slice(-8)}</span>{r.status !== "open" && <span class="st st-ok">{t("alm.tab." + r.status)}</span>}</div>
            <h2 style="margin:8px 0 4px;font-size:26px">{label(t, a.type)} · {p?.name || a.device_serial}</h2>
            <div style="color:var(--muted)">{site?.name || ""} · {t("x.alm.sub", { start: t.time(new Date(since)), peak: t.temp(peak), m: mins })}</div>
          </div>
          <div class="row">{p && <Link class="btn" href={"/cabinets/" + p.id}>{t("alm.openCab")}</Link>}{a.state === "open" && <button class="btn pri" type="button" onClick={ack}>{t("alm.ack")}</button>}</div>
        </div>
        {pts.length > 1 && <TimeChart points={pts} cls={cls} from={from} to={to} height={230} ticks="30m" intervals={[{ from: since, to: end, color: "var(--crit)", opacity: 0.08 }]} min={undefined} max={undefined} />}
      </section>
      <div class="grid" style="grid-template-columns:minmax(0,1.25fr) minmax(0,1fr)">
        <section class="card ai">
          <div class="card-h"><h2><Icon name="ai" size={20} />{t("x.alm.analysis")}</h2><span class="when">{t("x.alm.fromData")}</span></div>
          <ul>{analysis.map((x, i) => { const [h, ...rest] = x.split("|"); return <li key={i}><b>{h}</b> {rest.join("|")}</li>; })}</ul>
        </section>
        <section class="card">
          <div class="card-h"><h2>{t("x.alm.timeline")}</h2></div>
          <ol class="esc">
            <li class="done"><span class="b" /><div><b>{t.time(new Date(since))} · {t("x.alm.raised")}</b><small>{label(t, a.type)}</small></div></li>
            {a.ack_utc && <li class="done"><span class="b" /><div><b>{t.time(new Date(a.ack_utc))} · {t("alm.ackBy", { who: a.ack_by || "" })}</b><small>{a.ack_note || ""}</small></div></li>}
            {a.cleared_utc && <li class="done"><span class="b" /><div><b>{t.time(new Date(a.cleared_utc))} · {t("x.alarm.cleared")}</b><small>{t("x.alm.backInRange")}</small></div></li>}
            {r.close ? <li class="done"><span class="b" /><div><b>{t.time(new Date(r.close.occurred_at))} · {t("alm.closedBy")} {r.close.body.signer}</b><small>{r.close.body.meaning}</small></div></li>
              : <li class="cur"><span class="b" /><div><b>{t("alm.close")}</b><small>{t("alm.closeNote")}</small></div></li>}
          </ol>
        </section>
      </div>
      {r.status !== "closed" ? (
        <section class="card">
          <div class="card-h"><h2>{t("alm.close")}</h2><span style="color:var(--muted);font-size:13px">{t("alm.closeNote")}</span></div>
          <div class="g3" style="gap:14px">
            <label class="f">{t("alm.cause")}<select value={cause} onChange={(e) => setCause((e.target as HTMLSelectElement).value)}>{CAUSES.map((c) => <option key={c} value={c}>{t("alm.cause." + c)}</option>)}</select></label>
            <label class="f">{t("alm.action")}<select value={action} onChange={(e) => setAction((e.target as HTMLSelectElement).value)}>{ACTIONS.map((c) => <option key={c} value={c}>{t("alm.act." + c)}</option>)}</select></label>
            <label class="f">{t("alm.probe")}<input value={probe} placeholder="°C" onInput={(e) => setProbe((e.target as HTMLInputElement).value)} /></label>
          </div>
          <div class="lbl" style="margin:16px 0 8px">{t("alm.product")}</div>
          <div class="radios">{["none", "kept", "moved", "discarded"].map((x) => <label key={x} class={product === x ? "on" : ""}><input type="radio" name="product" checked={product === x} onChange={() => setProduct(x)} />{t("alm.prod." + x)}</label>)}</div>
          <div class="lbl" style="margin:16px 0 4px">{t("alm.record")}</div>
          <textarea class="ta" value={record} onInput={(e) => setRecord((e.target as HTMLTextAreaElement).value)} rows={3} />
          <div class="row" style="justify-content:flex-end;margin-top:12px"><button class="btn pri" type="button" onClick={() => setSign(true)}>{t("alm.closeBtn")}</button></div>
        </section>
      ) : (
        <section class="card"><div class="card-h"><h2>{t("alm.closedRec")}</h2><span class="st st-ok">{t("alm.signed")}</span></div>
          <dl class="kv" style="max-width:560px"><dt>{t("alm.cause")}</dt><dd>{t("alm.cause." + r.close!.body.cause)}</dd><dt>{t("alm.action")}</dt><dd>{t("alm.act." + r.close!.body.action)}</dd><dt>{t("alm.product")}</dt><dd>{t("alm.prod." + r.close!.body.product)}</dd>{r.close!.body.probe && <><dt>{t("alm.probe")}</dt><dd>{r.close!.body.probe} °C</dd></>}<dt>{t("alm.closedBy")}</dt><dd>{r.close!.body.signer} · {new Date(r.close!.occurred_at).toLocaleString(t.locale, { dateStyle: "short", timeStyle: "short" })}</dd></dl>
          {r.close!.body.record && <p style="margin:12px 0 0">{r.close!.body.record}</p>}
        </section>
      )}
      {sign && (
        <div class="modal" onClick={(e) => e.target === e.currentTarget && setSign(false)}>
          <form class="box card" onSubmit={close}>
            <h2 style="margin:0">{t("alm.signTitle")}</h2>
            <p style="margin:0;color:var(--muted)">{t("alm.signText", { id: a.id.slice(-8) })}</p>
            <label class="f">{t("alm.signer")}<input value={user.name || user.email} readOnly /></label>
            <label class="f">{t("alm.meaning")}<select value={meaning} onChange={(e) => setMeaning((e.target as HTMLSelectElement).value)}><option value="alm.meaning1">{t("alm.meaning1")}</option><option value="alm.meaning2">{t("alm.meaning2")}</option></select></label>
            <div class="row" style="justify-content:flex-end"><button class="btn" type="button" onClick={() => setSign(false)}>{t("alm.cancel")}</button><button class="btn pri" type="submit" disabled={busy}>{t("alm.signClose")}</button></div>
          </form>
        </div>
      )}
      {toast && <div class="toast" role="status">{toast}</div>}
    </div>
  );
}
